/**
 * The small set of pieces every console screen is built from.
 *
 * Keeping these here rather than inline per screen is what makes forty-odd
 * screens read as one product: a status badge means the same thing on the
 * admission funnel as on the archive deletion plan, and a page header is the
 * same shape whether the page is a live proxy or a table dump.
 */

import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, InfoCircle } from '@untitledui/icons';
import { Badge } from '../components/base/badges/badges';
import { cx } from '../utils/cx';
import { explainFailure } from './failures';
import { Bar } from './measures';
import { badgeColorFor, badgeColorForTone, type Tone } from './status';

export function PageHeader({
  title,
  description,
  actions,
  meta,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 border-b border-secondary pb-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-xl font-semibold text-primary">{title}</h1>
          {description ? <p className="max-w-3xl text-sm text-tertiary">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {meta ? <div className="flex flex-wrap items-center gap-x-6 gap-y-2">{meta}</div> : null}
    </header>
  );
}

/**
 * A lifecycle word as a badge.
 *
 * `value` is typed as a string but arrives from forty-odd control-plane reads,
 * and a row that simply does not carry the column is a real case. An absent
 * state reads as "not recorded" - which is honest, and distinguishable from any
 * state the system actually has - rather than throwing and taking the whole
 * screen down with it.
 */
export function StatusBadge({
  value,
  size = 'sm',
}: {
  value: string | null | undefined;
  size?: 'sm' | 'md';
}) {
  const label = value ? value.replace(/[_-]+/g, ' ') : 'not recorded';
  return (
    <Badge type="pill-color" size={size} color={badgeColorFor(value)}>
      {label}
    </Badge>
  );
}

const TONE_ICON: Record<Tone, FC<{ className?: string }>> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  info: InfoCircle,
  neutral: InfoCircle,
};

const TONE_SURFACE: Record<Tone, string> = {
  success: 'border-success-secondary bg-success-primary text-success-primary',
  warning: 'border-warning-secondary bg-warning-primary text-warning-primary',
  error: 'border-error-secondary bg-error-primary text-error-primary',
  info: 'border-brand-secondary bg-brand-primary text-brand-secondary',
  neutral: 'border-secondary bg-secondary text-tertiary',
};

/**
 * Render an API failure as a sentence the operator can act on, keeping the
 * server's code and reference id alongside it for support and the audit log.
 * Screens used to render the raw code, so a failed read read as `request_failed`
 * and nothing else.
 */
export function FailureText({ raw }: { raw: string | undefined }) {
  const failure = explainFailure(raw);
  if (!failure) return null;
  const detail = [failure.code, failure.reference ? `reference ${failure.reference}` : undefined]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      {failure.sentence}
      {detail ? <span className="block pt-1 text-xs opacity-75">{detail}</span> : null}
    </>
  );
}

/**
 * A standing condition the operator needs to act on — an unconnected archive,
 * a dark alert path — as opposed to a transient error from one request.
 */
export function Callout({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: Tone;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <section
      className={cx('flex gap-3 rounded-xl border p-4', TONE_SURFACE[tone])}
      role={tone === 'error' ? 'alert' : undefined}
    >
      <Icon className="mt-0.5 size-5 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-sm font-semibold">{title}</p>
        {children ? <div className="text-sm opacity-90">{children}</div> : null}
        {actions ? <div className="flex flex-wrap gap-2 pt-1">{actions}</div> : null}
      </div>
    </section>
  );
}

export function StatTile({
  label,
  value,
  hint,
  tone,
  statusLabel,
  bar,
  tooltip,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  /**
   * What the tone badge should say. The bare tone word ("warning") tells an
   * operator the colour, not the fact; a caller that knows why the tile is
   * amber should say so here.
   */
  statusLabel?: string;
  /**
   * A proportion this figure is part of, drawn under it. Shape answers before
   * the digits do, which is the whole reason a tile earns more than a number.
   */
  bar?: { fraction: number; label: string; tone?: Tone };
  /**
   * What the figure is counted over, for a label that cannot say it in two
   * words. A number whose population is invisible reads as a contradiction
   * the moment it sits beside one counted differently.
   */
  tooltip?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-xl border border-secondary bg-primary p-4">
      <span
        className={cx('text-xs font-medium text-tertiary uppercase', tooltip && 'cursor-help')}
        title={tooltip}
      >
        {label}
      </span>
      {/* The figure is the one thing on a tile that has to be readable, so it
          wraps rather than truncating: `145 accou…` and `7.14 per member p…`
          both shipped because this row clipped instead of growing. */}
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="min-w-0 text-display-xs font-semibold break-words text-primary">
          {value}
        </span>
        {tone ? (
          <Badge type="pill-color" size="sm" color={badgeColorForTone(tone)}>
            {statusLabel ?? tone}
          </Badge>
        ) : null}
      </div>
      {bar ? (
        <Bar fraction={bar.fraction} label={bar.label} tone={bar.tone ?? tone ?? 'info'} />
      ) : null}
      {hint ? <span className="text-xs text-tertiary">{hint}</span> : null}
    </div>
  );
}

export function StatRow({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>;
}

export function MetaItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-xs text-tertiary">{label}</span>
      <span className="truncate text-sm font-medium text-secondary">{value}</span>
    </div>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
}: {
  /** A node, so a heading can carry the tooltip for the one term inside it. */
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col">
          <h2 className="text-md font-semibold text-primary">{title}</h2>
          {description ? <p className="text-sm text-tertiary">{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Code({ children, className, ...props }: ComponentPropsWithoutRef<'code'>) {
  return (
    <code
      {...props}
      className={cx(
        'rounded-md bg-secondary px-1.5 py-0.5 font-mono text-xs text-secondary',
        className,
      )}
    >
      {children}
    </code>
  );
}
